// `/preview` — the staff testbed for the END-USER components
// (fn-48.5, R9). Simulates what the v2a embed ships to clients,
// inside the console for staff to exercise: list ↔ chat ↔ start.
//
// Console-chrome rules:
//   - The "Embed preview — staff testbed" banner is CONSOLE chrome —
//     it renders OUTSIDE the `.sbui-root` region so the simulated
//     embed surface contains ONLY end-user components.
//   - ONLY end-user components render inside the embed region
//     (`ConversationList` / `Chat` / `StartConversation`) — never the
//     operator views; `/inbox`, `/runs`, `/approvals` are untouched.
//   - No `matterId` is passed to `<StartConversation>`: the BFF mints
//     a synthetic matter in dev when absent — a documented dev-stack
//     convenience, NOT a prod capability. Real consumers (v2a embed
//     hosts) pass their matter context.
//   - `readOnly` is unreachable here (the CP lists only the caller's
//     own sessions post-fn-46), so `<Chat>` mounts without it.
//
// Sub-routes (deep-linkable; AuthGate `state.from` threading carries
// them across a login redirect):
//   - `/preview`      → conversation list
//   - `/preview/new`  → start-conversation flow
//   - `/preview/:id`  → chat (back → list; the list remounts and
//     refetches, so a just-started conversation shows up promptly)

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  Chat,
  ConversationList,
  PlatformProvider,
  StartConversation,
} from '@sb-ai-platform/ui-react';

import { getConsolePlatformAdapter } from '../../platform-adapter.js';
import { useMatterChatStart } from './use-matter-chat-start.js';

/**
 * The default `matter-creator` agent id (config unchanged — the fn-69
 * thesis: one agent, two kickoff-only modes). Env-overridable via
 * `VITE_MATTER_CREATOR_AGENT_ID` (per-environment) and falling back to the
 * dev-aus deployed agent so the demo works out of the box. Editable on the
 * matter-chat start form. The `import.meta.env` access is narrowed
 * defensively for node/test where it is absent (mirrors
 * `readBffOriginFromEnv`).
 */
function defaultMatterCreatorAgentId(): string {
  const fallback = 'b36e920e-4356-45c1-9ff5-02dfa7f76ee2';
  const meta = (import.meta as ImportMeta & { env?: Record<string, unknown> }).env;
  const raw = meta?.VITE_MATTER_CREATOR_AGENT_ID;
  return typeof raw === 'string' && raw.length > 0 ? raw : fallback;
}
const MATTER_CREATOR_AGENT_ID = defaultMatterCreatorAgentId();

function PreviewChrome(props: {
  readonly action?: React.ReactNode;
  readonly children: React.ReactNode;
}): React.ReactElement {
  return (
    <section className="page-preview">
      <div className="preview-banner">
        <span className="preview-banner-label">Embed preview — staff testbed</span>
        {props.action}
      </div>
      <div className="sbui-root preview-embed">
        <PlatformProvider adapter={getConsolePlatformAdapter()}>{props.children}</PlatformProvider>
      </div>
    </section>
  );
}

/** `/preview` — the end-user conversation list. */
export function PreviewPage(): React.ReactElement {
  const navigate = useNavigate();
  const toStart = (): void => {
    navigate('/preview/new');
  };
  return (
    <PreviewChrome
      action={
        <div className="preview-actions">
          <button
            type="button"
            className="btn btn--secondary"
            onClick={() => navigate('/preview/matter-chat')}
          >
            New matter chat
          </button>
          <button type="button" className="btn btn--primary" onClick={toStart}>
            New conversation
          </button>
        </div>
      }
    >
      <ConversationList
        onOpenConversation={(sessionId) => navigate(`/preview/${encodeURIComponent(sessionId)}`)}
        onStartConversation={toStart}
      />
    </PreviewChrome>
  );
}

/**
 * `/preview/matter-chat` — chat-mode start for the matter-creator demo
 * (fn-69.4, R1/R6). Paste a matter document, start the SAME deployed
 * `matter-creator` agent with the CONVERSATIONAL kickoff, then hand off to
 * `<Chat>` (where the `review_matter` confirmation card renders). The
 * headless mode is at `/archie`; this proves one-agent-two-modes.
 */
export function PreviewMatterChatStartPage(): React.ReactElement {
  const navigate = useNavigate();
  const [agentId, setAgentId] = useState(MATTER_CREATOR_AGENT_ID);
  const [document, setDocument] = useState('');
  const chat = useMatterChatStart((sessionId) =>
    navigate(`/preview/${encodeURIComponent(sessionId)}`),
  );

  const starting = chat.status === 'starting';
  const canStart = agentId.trim().length > 0 && document.trim().length > 0 && !starting;

  return (
    <PreviewChrome>
      <form
        className="preview-matter-chat"
        onSubmit={(e) => {
          e.preventDefault();
          if (canStart) chat.start({ agentId: agentId.trim(), document });
        }}
      >
        <h2 className="preview-matter-chat-heading">Start a matter chat</h2>
        <p className="preview-matter-chat-hint">
          The matter-creator agent, conversationally. Paste an intake document and it will analyse
          it, then show a matter for you to confirm before anything is created.
        </p>

        <label className="preview-matter-chat-label">
          Agent id
          <input
            type="text"
            className="preview-matter-chat-input"
            value={agentId}
            onChange={(e) => setAgentId(e.currentTarget.value)}
            disabled={starting}
          />
        </label>

        <label className="preview-matter-chat-label">
          Intake document
          <textarea
            className="preview-matter-chat-textarea"
            rows={10}
            placeholder="Paste the matter document text here…"
            value={document}
            onChange={(e) => setDocument(e.currentTarget.value)}
            disabled={starting}
          />
        </label>

        {chat.error !== undefined && (
          <p className="preview-matter-chat-error" role="alert">
            {chat.error}
          </p>
        )}

        <div className="preview-matter-chat-actions">
          <button type="submit" className="btn btn--primary" disabled={!canStart}>
            {starting ? 'Starting…' : 'Start matter chat'}
          </button>
          <button
            type="button"
            className="btn btn--tertiary"
            onClick={() => navigate('/preview')}
            disabled={starting}
          >
            Cancel
          </button>
        </div>
      </form>
    </PreviewChrome>
  );
}

/** `/preview/new` — the start-conversation flow. */
export function PreviewStartPage(): React.ReactElement {
  const navigate = useNavigate();
  return (
    <PreviewChrome>
      <StartConversation
        onStarted={(sessionId) => navigate(`/preview/${encodeURIComponent(sessionId)}`)}
        onCancel={() => navigate('/preview')}
      />
    </PreviewChrome>
  );
}

/** `/preview/:id` — the end-user chat for one conversation. */
export function PreviewChatPage(): React.ReactElement {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const sessionId = params.id ?? '';

  if (sessionId.length === 0) {
    return (
      <section className="page-preview" role="alert">
        <h1>Not found</h1>
        <p>No conversation id in the URL.</p>
      </section>
    );
  }

  return (
    <PreviewChrome>
      <Chat sessionId={sessionId} onBack={() => navigate('/preview')} />
    </PreviewChrome>
  );
}
